import { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../utils/api';
import { getErrorMessage } from '../utils/error';

// Sub-components
import BookingLoading from './booking/BookingLoading';
import BookingError from './booking/BookingError';
import BookingSuccess from './booking/BookingSuccess';
import BookingForm from './booking/BookingForm';

import { BookingInfo } from './booking/BookingTypes';

const BookingPage: FC = () => {
    const params = useParams();
    // Support both :token and wildcard *
    const token = params.token || params['*'];

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [info, setInfo] = useState<BookingInfo | null>(null);
    const [success, setSuccess] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Form selection states
    const [selectedDate, setSelectedDate] = useState<string>('');
    const [selectedTime, setSelectedTime] = useState<string>('');
    const [currentMonth, setCurrentMonth] = useState(new Date());

    // Slots states
    const [availableSlots, setAvailableSlots] = useState<{ start: string; end: string; status: 'available' | 'busy' }[]>([]);
    const [fetchingAvailability, setFetchingAvailability] = useState(false);

    // Initial load
    useEffect(() => {
        if (!token) {
            setError('Invalid booking link.');
            setLoading(false);
            return;
        }
        api.get(`/booking/${token}`)
            .then(res => setInfo(res.data))
            .catch(err => {
                console.error("Booking load failed:", err);
                setError(err.response?.data?.message || 'This booking link is invalid or expired.');
            })
            .finally(() => setLoading(false));
    }, [token]);

    // Availability Fetch
    useEffect(() => {
        if (!token || !selectedDate) return;

        const fetchAvailability = async () => {
            setFetchingAvailability(true);
            setAvailableSlots([]);
            try {
                const start = `${selectedDate}T00:00:00Z`;
                const end = `${selectedDate}T23:59:59Z`;
                const { data } = await api.get(`/booking/${token}/slots`, {
                    params: { startDate: start, endDate: end }
                });
                setAvailableSlots(data || []);
            } catch (err) {
                console.error("Failed to fetch slots:", err);
            } finally {
                setFetchingAvailability(false);
            }
        };

        fetchAvailability();
    }, [token, selectedDate]);

    const handleSubmit = async () => {
        if (!selectedDate || !selectedTime || !token) return;
        setSubmitting(true);
        setError(null);
        try {
            await api.post(`/booking/confirm`, { token, date: selectedDate, slotStart: selectedTime });
            setSuccess(true);
        } catch (err: unknown) {
            setError(getErrorMessage(err));
        } finally {
            setSubmitting(false);
        }
    };

    // Color handling is now centralized in BookingStyles.ts

    // State management for transitions
    if (loading) return <BookingLoading />;
    if (!info) return <BookingError error={error || 'Booking information not available.'} />;

    if (success && info) return (
        <BookingSuccess
            selectedDate={selectedDate}
            selectedTime={selectedTime}
            company={info.company}
            user={info.user}
        />
    );

    return (
        <BookingForm
            info={info}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            selectedTime={selectedTime}
            setSelectedTime={setSelectedTime}
            availableSlots={availableSlots}
            fetchingAvailability={fetchingAvailability}
            currentMonth={currentMonth}
            setCurrentMonth={setCurrentMonth}
            submitting={submitting}
            onSubmit={handleSubmit}
        />
    );
};

export default BookingPage;
